/**
 * Module: Shared Artifact Types
 *
 * Cross-package contract for AI-generated artifacts. The server emits the
 * stream events defined here through the AI SDK's UIMessageStreamWriter; the
 * client maps them into UIArtifact state for the canvas overlay and inline
 * preview cards. Keep this file dependency-free so both the @taqat/ai server
 * code and the browser client can import it.
 */

/**
 * Artifact kinds the client and server agree on.
 *
 * - `document`     — markdown prose rendered in a TipTap canvas.
 * - `html`         — standalone HTML rendered inside a sandboxed iframe.
 * - `presentation` — slide deck (HTML <section> per slide) rendered with a
 *                    thumbnail sidebar + stage view.
 * - `image`        — raster/vector image. The body is a JSON payload —
 *                    `{ url, mimeType, width?, height?, sizeBytes?, prompt?,
 *                    model?, seed? }` — so generation lineage travels with
 *                    the row. The viewer parses it on the client.
 * - `spreadsheet`  — tabular data. The body is raw CSV text (RFC 4180); the
 *                    viewer parses it client-side and exports re-emit it as
 *                    `.csv` or `.xlsx`.
 *
 * The wire format does not change per kind; the renderer dispatches off this
 * value once events arrive on the client.
 */
export type ArtifactKind =
  | "document"
  | "html"
  | "presentation"
  | "image"
  | "spreadsheet";

/**
 * Mode flag carried by the init event so the client can distinguish a fresh
 * stream from a follow-up rewrite. Creates blank the canvas and auto-open it;
 * updates buffer the new body invisibly and swap on finish.
 */
export type ArtifactStreamMode = "create" | "update";

/**
 * Discriminated union of every event the artifact runtime writes onto the
 * UIMessageStreamWriter. The `data-` prefix matches the AI SDK convention so
 * `useChat({ onData })` receives them alongside other stream metadata.
 *
 * Started as three events (init/delta/finish) — no JSON-shape artifact
 * existed in v1, so streaming was always token-safe markdown that could
 * append directly. `snapshot` is the "layer a buffered swap on top" this
 * comment originally predicted: `image` artifacts needed a JSON-shaped body
 * before v2 did, and initially got it by sending ONE `delta` carrying a
 * pre-stringified JSON blob — technically working (a single delta never hits
 * the "can't append partial JSON" problem) but semantically dishonest, since
 * `delta` means "text chunk to append" everywhere else. `snapshot` names what
 * it actually was: one atomic non-text payload, not a chunk.
 */
export type ArtifactStreamEvent =
  | {
      type: "data-artifact-init";
      data: {
        /** Stable artifact id used by the canvas, REST, and rehydration. */
        id: string;
        /** Human-readable title shown on the canvas + inline preview cards. */
        title: string;
        /** Distinguishes a brand-new artifact from an in-place rewrite. */
        mode: ArtifactStreamMode;
        /** Artifact kind so the client picks the right renderer immediately. */
        kind: ArtifactKind;
      };
    }
  | {
      type: "data-artifact-delta";
      data: {
        /**
         * Artifact this chunk belongs to. Lets the client drop stragglers from
         * a different, concurrently-streaming artifact instead of appending
         * them to whatever is currently open (which mixed markdown into a
         * spreadsheet when two artifacts generated in one turn).
         */
        id: string;
        /** Markdown chunk to append (or buffer, when an update is in flight). */
        chunk: string;
      };
    }
  | {
      type: "data-artifact-slide-plan";
      data: {
        /** Final slide order (ids) for the deck after the edit. */
        order: string[];
        /** Ids of the slides that will change — the client scopes its loader to these. */
        editing: string[];
      };
    }
  | {
      type: "data-artifact-slide-delta";
      data: {
        /** Slide the chunk belongs to. */
        slideId: string;
        /** Incremental `<section>` html for that slide (append on the client). */
        chunk: string;
      };
    }
  | {
      type: "data-artifact-slide-finish";
      data: {
        /** Slide that finished streaming. */
        slideId: string;
        /** Final full `<section>` html — replaces any accumulated partial. */
        html: string;
      };
    }
  | {
      type: "data-artifact-snapshot";
      data: {
        /**
         * Artifact this snapshot belongs to — same stragglers-guard purpose as
         * `data-artifact-delta`'s `id`.
         */
        id: string;
        /**
         * The complete structured payload, e.g. an `ImageArtifactContent`
         * object. NOT text to append — this event exists precisely because a
         * JSON-shaped body is not token-safe markdown and must not be spread
         * across incremental `delta` chunks (or faked as a single delta
         * carrying a pre-stringified blob, which is what image artifacts did
         * before this event existed). One snapshot replaces the artifact's
         * content atomically; there is no accumulation.
         */
        data: unknown;
      };
    }
  | {
      type: "data-artifact-replace";
      data: {
        /** Artifact whose body is being replaced — same stragglers-guard as `delta`. */
        id: string;
        /**
         * The complete new body, replacing whatever the client has accumulated
         * rather than appending to it.
         *
         * Exists because a `create` stream's body is built by APPENDING deltas,
         * so the client has no way to hear "that deck you just watched stream
         * in has been corrected". The presentation handler's structure-repair
         * pass (deck outside its template's slide bounds) rewrites the body
         * server-side after the stream ends; without this event the repaired
         * deck would reach the database while the user kept looking at the
         * unrepaired one until a reload.
         *
         * Distinct from `snapshot`, which carries a JSON VALUE the client
         * stringifies (image payloads). This carries the final text body
         * verbatim.
         */
        content: string;
      };
    }
  | {
      type: "data-artifact-finish";
      data: null;
    }
  | {
      type: "data-artifact-error";
      data: {
        /** Artifact id the failure applies to, when known. */
        id?: string;
        /** User-facing, non-leaky message rendered in the canvas error banner. */
        message: string;
        /**
         * The original update instruction, echoed back so the client can offer
         * a precise one-click retry that re-issues the same edit.
         */
        instructions?: string;
      };
    };

/**
 * Stream event type literals — exported separately so client switch statements
 * can use them as constants without re-deriving the strings.
 */
export const ARTIFACT_STREAM_TYPES = {
  INIT: "data-artifact-init",
  DELTA: "data-artifact-delta",
  /** Atomic JSON-shaped payload — see the union member's doc comment. */
  SNAPSHOT: "data-artifact-snapshot",
  /** Whole-body replacement of an already-streamed artifact — see the union member. */
  REPLACE: "data-artifact-replace",
  FINISH: "data-artifact-finish",
  ERROR: "data-artifact-error",
  /**
   * Presentation-only slide-scoped events. They let an in-place deck edit show
   * a loader (and stream) on just the slide being changed instead of blanking
   * the whole deck. Other kinds ignore them and use the buffered whole-body
   * swap (INIT/DELTA/FINISH).
   */
  SLIDE_PLAN: "data-artifact-slide-plan",
  SLIDE_DELTA: "data-artifact-slide-delta",
  SLIDE_FINISH: "data-artifact-slide-finish",
} as const;

/**
 * Tool part type names used in chat tool-call rendering. Mirrors the AI SDK
 * convention of prefixing tool ids with `tool-` in UI message parts. Both the
 * cross-message render-mode resolver and the inline tool renderer use this.
 */
export const ARTIFACT_TOOL_NAMES = {
  CREATE: "create_artifact",
  UPDATE: "update_artifact",
  GENERATE_IMAGE: "generate_image",
} as const;

/**
 * Live per-slide state for a presentation update, driven by the slide-scoped
 * stream events. Present (non-null) only while a deck edit is in flight and the
 * server has announced its plan; lets the canvas keep untouched slides on
 * screen and show the loader/stream on just the slide(s) being changed.
 */
export interface PendingSlides {
  /** Final slide order (ids) for the deck after the edit. */
  order: string[];
  /** Ids the server flagged as changing — used to scope the loader. */
  editing: string[];
  /** Accumulated html per streaming slide; `done` flips true on slide-finish. */
  byId: Record<string, { html: string; done: boolean }>;
}

/**
 * Client-side canvas state for the open artifact. Mirrors the studio's
 * UIArtifact contract minus the per-kind buffered-swap field — markdown can
 * append safely so we just track a single content string + streaming flag.
 */
export interface UIArtifact {
  /** Stable artifact id; empty string means "no artifact loaded". */
  id: string;
  /** Title shown in the canvas header. */
  title: string;
  /** Artifact kind — drives renderer selection in the canvas. */
  kind: ArtifactKind;
  /** Latest visible markdown body. */
  content: string;
  /**
   * Replacement body buffered during an `update` stream. `null` means content
   * deltas land directly in `content`. A non-null value (even empty string)
   * means an update is in flight; deltas accumulate here and are swapped into
   * `content` on the finish event so the canvas never blanks mid-rewrite.
   */
  pendingContent: string | null;
  /**
   * Live per-slide edit state for presentation updates. `null` for every other
   * kind and whenever no slide-scoped edit is in flight. When set, the
   * presentation canvas renders from this (untouched slides from `content`,
   * changed slides streamed here) instead of a full-deck loader.
   */
  pendingSlides: PendingSlides | null;
  /**
   * Snapshot of `content` taken at the moment an update stream finished and
   * the new body became visible. Used to compute a diff and highlight the
   * changed regions in the canvas, Gemini-style. Cleared after the
   * highlight fade window so subsequent reads see only the clean content.
   * `null` means no highlight is active.
   */
  previousContent: string | null;
  /** Whether the canvas overlay is visible. */
  isVisible: boolean;
  /**
   * Streaming lifecycle marker. `streaming` while events are arriving,
   * `error` when a create/update stream failed (the previous body is kept
   * intact and an error banner is shown with a retry affordance).
   */
  status: "streaming" | "idle" | "error";
  /**
   * User-facing message for the error banner. Non-null only while
   * `status === "error"`; cleared when a new stream starts or the user
   * dismisses the banner.
   */
  error: string | null;
  /**
   * The instruction behind the last update, captured from the failure event
   * so the error banner's "Retry" can re-issue the exact same edit. `null`
   * when there is nothing to retry.
   */
  lastInstruction: string | null;
  /**
   * Trigger element bounding box used for the open/close animation. The
   * canvas expands from this rect when shown and collapses back when closed.
   */
  boundingBox: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

/**
 * Tool output shape persisted in the chat message store. Matches the success
 * return value of the create/update artifact tools so inline preview cards
 * have everything they need without an extra fetch.
 */
export interface ArtifactToolResult {
  id: string;
  title: string;
  kind: ArtifactKind;
  content: string;
  version: number;
  /** Optional one-line summary of the create/update for the inline card. */
  message?: string;
}
